import type { FC, FormEvent, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

import { campaign } from '@/constants/campaign'

type FieldName = 'firstName' | 'lastName' | 'email'
type FieldErrors = Partial<Record<FieldName, string>>
type Status = 'idle' | 'submitting' | 'success'

const copy = campaign.register

const FIELDS: ReadonlyArray<{ name: FieldName, label: string, type: string, autoComplete: string }> = [
    { name: 'firstName', label: copy.fields.firstName, type: 'text', autoComplete: 'given-name' },
    { name: 'lastName', label: copy.fields.lastName, type: 'text', autoComplete: 'family-name' },
    { name: 'email', label: copy.fields.email, type: 'email', autoComplete: 'email' }
]

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const validate = (values: Record<FieldName, string>): FieldErrors => {
    const errors: FieldErrors = {}

    if (!values.firstName.trim()) errors.firstName = copy.errors.firstNameRequired
    if (!values.lastName.trim()) errors.lastName = copy.errors.lastNameRequired
    if (!values.email.trim()) errors.email = copy.errors.emailRequired
    else if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = copy.errors.emailInvalid

    return errors
}

interface RegistrationFormProps {
    onSuccess?: () => void
}

const RegistrationForm: FC<RegistrationFormProps> = ({ onSuccess }): ReactNode => {
    const [values, setValues] = useState<Record<FieldName, string>>({ firstName: '', lastName: '', email: '' })
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
    const [formError, setFormError] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const startedAt = useRef<number | null>(null)
    const submitting = useRef(false)
    const successRef = useRef<HTMLDivElement>(null)
    const inputRefs = useRef<Partial<Record<FieldName, HTMLInputElement | null>>>({})

    useEffect(() => {
        startedAt.current = performance.now()
    }, [])

    useEffect(() => {
        if (status === 'success') successRef.current?.focus()
    }, [status])

    const update = (name: FieldName, value: string) => {
        setValues(current => ({ ...current, [name]: value }))
        if (fieldErrors[name]) setFieldErrors(current => ({ ...current, [name]: undefined }))
    }

    const focusFirstError = (errors: FieldErrors) => {
        const first = FIELDS.find(field => errors[field.name])
        if (first) inputRefs.current[first.name]?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        // Ignore extra clicks while a submission is already on its way.
        if (submitting.current) return

        setFormError('')

        const errors = validate(values)

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors)
            focusFirstError(errors)
            return
        }

        const honeypot = (event.currentTarget.elements.namedItem('website') as HTMLInputElement | null)?.value ?? ''

        submitting.current = true
        setStatus('submitting')

        try {
            const response = await fetch('/api/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...values,
                    website: honeypot,
                    elapsed: startedAt.current === null ? 0 : Math.round(performance.now() - startedAt.current)
                })
            })

            const result = await response.json().catch(() => null) as { message?: string, fieldErrors?: FieldErrors } | null

            if (response.ok) {
                setStatus('success')
                onSuccess?.()
                return
            }

            const serverErrors = result?.fieldErrors ?? {}

            setFieldErrors(serverErrors)
            setFormError(Object.keys(serverErrors).length > 0 ? '' : result?.message || copy.errors.generic)
            focusFirstError(serverErrors)
            setStatus('idle')
        }
        catch {
            setFormError(copy.errors.generic)
            setStatus('idle')
        }
        finally {
            submitting.current = false
        }
    }

    if (status === 'success') {
        return (
            <div
                ref={successRef}
                tabIndex={-1}
                role='status'
                className='text-center outline-none motion-safe:animate-in motion-safe:fade-in motion-safe:duration-700'
            >
                <p className='font-heading text-[length:clamp(1.5rem,5vw,2.25rem)] font-bold leading-tight tracking-[0.02em] text-white'>
                    {copy.success}
                </p>
                <p className='mt-4 text-balance text-base leading-relaxed text-foreground/80'>
                    {copy.arrival}
                </p>
            </div>
        )
    }

    const isSubmitting = status === 'submitting'

    return (
        <form noValidate onSubmit={handleSubmit} aria-busy={isSubmitting} className='flex w-full flex-col gap-5 text-left'>
            {FIELDS.map(field => {
                const error = fieldErrors[field.name]
                const errorId = `${field.name}-error`

                return (
                    <div key={field.name} className='flex flex-col gap-2'>
                        <label htmlFor={field.name} className='font-heading text-xs font-semibold tracking-[0.24em] text-foreground/80 uppercase'>
                            {field.label}
                            <span aria-hidden='true' className='text-primary'> *</span>
                        </label>

                        <input
                            ref={element => { inputRefs.current[field.name] = element }}
                            id={field.name}
                            name={field.name}
                            type={field.type}
                            autoComplete={field.autoComplete}
                            required
                            maxLength={field.name === 'email' ? 254 : 80}
                            value={values[field.name]}
                            onChange={event => update(field.name, event.target.value)}
                            disabled={isSubmitting}
                            aria-invalid={error ? true : undefined}
                            aria-describedby={error ? errorId : undefined}
                            className={cn(
                                'h-12 w-full rounded-sm border bg-white/[0.04] px-4 text-base text-white transition-colors duration-200',
                                'placeholder:text-foreground/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
                                'disabled:opacity-60',
                                error ? 'border-primary' : 'border-white/15 hover:border-white/30'
                            )}
                        />

                        {error && (
                            <p id={errorId} className='text-sm text-[#FF6B85]'>
                                {error}
                            </p>
                        )}
                    </div>
                )
            })}

            {/* Hidden from people; bots that fill it in are ignored. */}
            <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                <label htmlFor='website'>Website</label>
                <input id='website' name='website' type='text' tabIndex={-1} autoComplete='off' defaultValue='' />
            </div>

            {formError && (
                <p role='alert' className='rounded-sm border border-primary/60 bg-primary/10 px-4 py-3 text-sm text-white'>
                    {formError}
                </p>
            )}

            <button
                type='submit'
                disabled={isSubmitting}
                className={cn(
                    'mt-2 inline-flex h-12 cursor-pointer w-full items-center justify-center gap-3 rounded-sm bg-primary px-7 font-heading text-[0.9375rem] font-bold leading-none tracking-[0.2em] text-primary-foreground sm:h-14 sm:text-base',
                    'transition-[filter,box-shadow] duration-300 ease-out hover:shadow-[0_14px_44px_-14px_var(--primary)] hover:brightness-110 active:brightness-95',
                    'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white',
                    'disabled:cursor-wait disabled:brightness-90'
                )}
            >
                {isSubmitting && <LoaderCircle aria-hidden='true' className='size-4 motion-safe:animate-spin' />}
                <span className='-mr-[0.2em]'>
                    {isSubmitting ? copy.submitting : copy.submit}
                </span>
            </button>

            <p aria-live='polite' className='sr-only'>
                {isSubmitting ? copy.submitting : ''}
            </p>
        </form>
    )
}

export default RegistrationForm
